import * as React from 'react';
import { Controller, type FieldValues } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { useFormField } from './Form';

/* ---------- shared ---------- */

type BaseProps = {
  name: string;
  label: string;
  description?: string;
  disabled?: boolean;
  className?: string;
};

function FieldShell({
  id,
  label,
  description,
  descriptionId,
  errorId,
  error,
  className,
  children,
}: {
  id: string;
  label: string;
  description?: string;
  descriptionId: string;
  errorId: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {description && !error && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/* ---------- TextField ---------- */

type TextFieldProps = BaseProps & {
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number';
  autoComplete?: string;
};

export function TextField({
  name,
  label,
  description,
  disabled,
  placeholder,
  type = 'text',
  autoComplete,
  className,
}: TextFieldProps): React.ReactElement {
  const { form, id, descriptionId, errorId, error } =
    useFormField<FieldValues>(name);
  const register = form.register(name, {
    valueAsNumber: type === 'number',
  });
  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      descriptionId={descriptionId}
      errorId={errorId}
      error={error}
      className={className}
    >
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : description ? descriptionId : undefined}
        {...register}
      />
    </FieldShell>
  );
}

/* ---------- TextareaField ---------- */

type TextareaFieldProps = BaseProps & {
  placeholder?: string;
  rows?: number;
};

export function TextareaField({
  name,
  label,
  description,
  disabled,
  placeholder,
  rows = 5,
  className,
}: TextareaFieldProps): React.ReactElement {
  const { form, id, descriptionId, errorId, error } =
    useFormField<FieldValues>(name);
  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      descriptionId={descriptionId}
      errorId={errorId}
      error={error}
      className={className}
    >
      <Textarea
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : description ? descriptionId : undefined}
        {...form.register(name)}
      />
    </FieldShell>
  );
}

/* ---------- SelectField ---------- */

type SelectOption = { value: string; label: string };

type SelectFieldProps = BaseProps & {
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
};

export function SelectField({
  name,
  label,
  description,
  disabled,
  options,
  placeholder,
  className,
}: SelectFieldProps): React.ReactElement {
  const { form, id, descriptionId, errorId, error } =
    useFormField<FieldValues>(name);
  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      descriptionId={descriptionId}
      errorId={errorId}
      error={error}
      className={className}
    >
      <Controller
        control={form.control}
        name={name}
        render={({ field }) => (
          <Select
            value={field.value ?? ''}
            onValueChange={field.onChange}
            disabled={disabled}
          >
            <SelectTrigger
              id={id}
              aria-invalid={error ? true : undefined}
              aria-describedby={
                error ? errorId : description ? descriptionId : undefined
              }
              onBlur={field.onBlur}
            >
              <SelectValue placeholder={placeholder ?? 'Select…'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </FieldShell>
  );
}

/* ---------- SwitchField ---------- */

type SwitchFieldProps = BaseProps;

export function SwitchField({
  name,
  label,
  description,
  disabled,
  className,
}: SwitchFieldProps): React.ReactElement {
  const { form, id, descriptionId, errorId, error } =
    useFormField<FieldValues>(name);
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id} className="cursor-pointer">
          {label}
        </Label>
        <Controller
          control={form.control}
          name={name}
          render={({ field }) => (
            <Switch
              id={id}
              checked={Boolean(field.value)}
              onCheckedChange={field.onChange}
              onBlur={field.onBlur}
              disabled={disabled}
              aria-invalid={error ? true : undefined}
              aria-describedby={
                error ? errorId : description ? descriptionId : undefined
              }
            />
          )}
        />
      </div>
      {description && !error && (
        <p id={descriptionId} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
